'use client';

import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 60_000; // 1 минута
const RELOAD_DELAY_MS = 30_000; // 30 секунд до авто-перезагрузки

type VersionResponse = {
  buildId: string;
  ts: number;
};

async function fetchBuildId(signal: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch('/api/version', {
      cache: 'no-store',
      signal,
      headers: { 'X-Deployment-Watcher': '1' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionResponse;
    return data.buildId || null;
  } catch {
    return null;
  }
}

/**
 * DeploymentWatcher — клиентский компонент, который опрашивает /api/version
 * и показывает уведомление при обнаружении новой версии приложения.
 *
 * Логика:
 * - При монтировании запоминает текущий BUILD_ID.
 * - Раз в POLL_INTERVAL_MS опрашивает сервер.
 * - При несовпадении показывает toast "Доступна новая версия".
 * - Через RELOAD_DELAY_MS делает window.location.reload() автоматически.
 * - Пользователь может нажать "Обновить сейчас" или "Позже".
 *
 * Это устраняет необходимость в hard refresh после деплоя.
 */
export function DeploymentWatcher() {
  const initialBuildId = useRef<string | null>(null);
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [notified, setNotified] = useState(false);

  useEffect(() => {
    // В dev-режиме отключаем — там HMR
    if (process.env.NODE_ENV !== 'production') return;

    const controller = new AbortController();
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const performReload = () => {
      // Очищаем кэш Service Worker если он есть
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.unregister());
        });
      }
      window.location.reload();
    };

    const showUpdateToast = () => {
      if (notified) return;
      setNotified(true);

      // Запускаем таймер авто-перезагрузки
      reloadTimerRef.current = setTimeout(performReload, RELOAD_DELAY_MS);

      toast.info('Доступна новая версия приложения', {
        description: `Страница автоматически обновится через ${RELOAD_DELAY_MS / 1000} секунд.`,
        duration: RELOAD_DELAY_MS,
        action: {
          label: 'Обновить сейчас',
          onClick: () => {
            if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
            performReload();
          },
        },
        cancel: {
          label: 'Позже',
          onClick: () => {
            if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
            // При следующем переходе по ссылке Next.js Link сам подтянет новый bundle.
          },
        },
      });
    };

    const check = async () => {
      if (cancelled) return;
      const buildId = await fetchBuildId(controller.signal);
      if (!buildId || cancelled) return;

      if (initialBuildId.current === null) {
        initialBuildId.current = buildId;
        return;
      }

      if (buildId !== initialBuildId.current) {
        showUpdateToast();
        // Перестаём опрашивать после уведомления
        if (intervalId) clearInterval(intervalId);
      }
    };

    // Первый запуск — сохраняем BUILD_ID
    void check();

    intervalId = setInterval(() => {
      // Не опрашиваем если вкладка скрыта (экономим ресурсы)
      if (document.visibilityState === 'visible') {
        void check();
      }
    }, POLL_INTERVAL_MS);

    // При возврате на вкладку — проверяем сразу
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      controller.abort();
      if (intervalId) clearInterval(intervalId);
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [notified]);

  return null;
}
